import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

export default function AuthenticationShell({
  children,
  appName = "Abra Controls Directive 83",
  header = "FPZ Reporting",
  tagline = "FPZ reports, save, share, analyze",
  bullets = [
    "Generate new FPZ reports in a guided flow",
    "Save reports to a library for quick retrieval",
    "Share reports to collaborators and across teams",
    "Create work orders from reports to streamline operations",
  ],
  heroImageUrl,
  onSubmit,
  errorText,
  footerNote = "Abra Controls Directive 83",
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit) return;

    try {
      setSubmitting(true);
      await onSubmit({ email, password });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
        }}
      >
        {/* LEFT: product info */}
        <Box
          sx={{
            p: { xs: 3, md: 6 },
            display: "flex",
            alignItems: "stretch",
            justifyContent: "center",
            backgroundImage: heroImageUrl
              ? `url(${heroImageUrl})`
              : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: heroImageUrl ? "brightness(0.55)" : "none",
            color: "common.white",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 760,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 0,
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant={isDesktop ? "h5" : "h6"} fontWeight={800} noWrap>
                  {appName}
                </Typography>
                <Typography variant="body2" noWrap>
                  Spottertech
                </Typography>
              </Box>
            </Box>
            <Box>

              <Typography
                variant={isDesktop ? "h3" : "h4"}
                fontWeight={900}
                sx={{ letterSpacing: -0.5, mb: 2 }}
              >
                {header}
              </Typography>

              <Typography variant="body1" sx={{ maxWidth: 620, }}>
                {tagline}
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                  What you can do
                </Typography>

                <Stack spacing={1.25}>
                  {bullets.map((b, idx) => (
                    <Box
                      key={`${idx}-${b}`}
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 10,
                          bgcolor: "grey.100",
                          mt: "7px",
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {b}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ pt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography variant="caption">
                  {footerNote}
                </Typography>
                <Stack direction="row" spacing={2}>
                  {/*<Link underline="hover" variant="caption" href="/privacy">
                    Privacy
                  </Link>
                  <Link underline="hover" variant="caption" href="/terms">
                    Terms
                  </Link>*/}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* RIGHT: login form */}
        <Box
          sx={{
            p: { xs: 3, md: 6 },
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "center",
            borderLeft: { xs: "none", md: `1px solid ${theme.palette.divider}` },
            bgcolor: { xs: "background.default", md: "background.paper" },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {children}
              </CardContent>
            </Card>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2, textAlign: "center" }}
            >
              {appName} • Secure access required
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

AuthenticationShell.propTypes = {
  appName: PropTypes.string,
  tagline: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func,
  errorText: PropTypes.string,
  footerNote: PropTypes.string,
};
